// Realtime connection manager: one fetch-streamed SSE connection, a ref-counted
// topic registry, batched subscribe/unsubscribe POSTs, and auto-reconnect.
//
// Framework-free on purpose — RealtimeProvider is a thin React wrapper, and the
// tests drive this directly with a mocked fetch/client.
//
// Protocol (docs/api-contract.md §6):
//   GET  {baseUrl}/api/v1/events                     → text/event-stream
//     first frame:  event: hello / data: {"connection_id": "..."}
//     heartbeat:    comment frame every 20s (>45s of silence = dead connection)
//     events:       id/event/data frames; data is {"topic": ..., "type": ..., "data": {...}}
//   POST {baseUrl}/api/v1/events/{id}/subscriptions  body {"add": [...], "remove": [...]}
//     → {"results": {"<topic>": "granted"|"denied"}} (verdicts for adds only)
//
// Lifecycle choices (documented for Phase 5):
// - The connection opens lazily on the first subscriber (signed-in users always
//   have one: SessionRealtimeBridge subscribes to their account topic).
// - The connection stays open after the last unsubscribe (topics are removed
//   server-side; reopening per-subscriber would thrash on route changes).
// - Reconnect: exponential backoff 1s→30s, fresh Clerk token, new hello/
//   connection_id, full re-subscribe of the current topic set, then every
//   subscriber's onReconnect fires so live queries refetch.
// - 401 on connect: retried once immediately with getToken({skipCache: true}),
//   then falls into normal backoff (every attempt fetches a fresh token anyway).

import { createSseParser } from './sse.js'

export function createRealtimeManager({
  baseUrl,
  getToken, // async (opts?) => token | null
  client, // api client (createApiClient) — used for subscription POSTs
  fetchImpl = (...args) => fetch(...args),
  backoffBaseMs = 1000,
  backoffMaxMs = 30000,
  heartbeatTimeoutMs = 45000,
  watchdogIntervalMs = 10000,
} = {}) {
  const registrations = new Map() // topic → Set<{onEvent, onReconnect}>
  const granted = new Set() // topics the server acked as granted (or removal-pending)
  const denied = new Set() // topics denied this connection — not retried until reconnect

  let connectionId = null
  let abortController = null
  let status = 'idle' // idle | connecting | open | reconnecting | closed
  let closed = false
  let everConnected = false
  let attempts = 0
  let flushScheduled = false
  let flushing = false
  let lastActivity = 0
  let watchdogTimer = null
  let reconnectTimer = null

  const statusListeners = new Set()

  function setStatus(next) {
    if (status === next) return
    status = next
    for (const fn of [...statusListeners]) {
      try {
        fn(next)
      } catch (err) {
        console.error('[realtime] status listener failed', err)
      }
    }
  }

  function subscribe(topic, onEvent, onReconnect) {
    let set = registrations.get(topic)
    if (!set) {
      set = new Set()
      registrations.set(topic, set)
    }
    const reg = { onEvent, onReconnect }
    set.add(reg)
    ensureConnected()
    scheduleFlush()

    let removed = false
    return function unsubscribe() {
      if (removed) return
      removed = true
      const current = registrations.get(topic)
      if (!current) return
      current.delete(reg)
      if (current.size === 0) {
        registrations.delete(topic)
        denied.delete(topic)
        scheduleFlush()
      }
    }
  }

  function ensureConnected() {
    if (closed || abortController || reconnectTimer) return
    connect()
  }

  async function connect() {
    if (closed) return
    const ac = new AbortController()
    abortController = ac
    setStatus(everConnected ? 'reconnecting' : 'connecting')

    try {
      let res = await openStream(await safeGetToken(), ac.signal)
      if (res.status === 401) {
        // token may be stale — retry once immediately with a forced-fresh token
        res = await openStream(await safeGetToken({ skipCache: true }), ac.signal)
      }
      if (!res.ok || !res.body) {
        const err = new Error(`SSE connect failed (${res.status})`)
        err.status = res.status
        throw err
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      const parser = createSseParser(handleFrame)
      lastActivity = Date.now()
      startWatchdog(ac)

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        lastActivity = Date.now()
        parser.feed(typeof value === 'string' ? value : decoder.decode(value, { stream: true }))
      }
      // clean end-of-stream from the server → treat as a drop
    } catch (err) {
      if (!ac.signal.aborted) console.warn('[realtime] connection error', err)
    } finally {
      stopWatchdog()
    }

    // If this connection was superseded (reset) or the manager closed, stop here.
    if (closed || abortController !== ac) return
    abortController = null
    connectionId = null
    granted.clear()
    scheduleReconnect()
  }

  function openStream(token, signal) {
    const headers = { Accept: 'text/event-stream' }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetchImpl(`${baseUrl}/api/v1/events`, { headers, signal, cache: 'no-store' })
  }

  async function safeGetToken(opts) {
    try {
      return await getToken?.(opts)
    } catch {
      return null
    }
  }

  function handleFrame(frame) {
    if (frame.event === 'hello') {
      let payload = {}
      try {
        payload = JSON.parse(frame.data)
      } catch {
        // ignore malformed hello — watchdog will recycle the connection
      }
      if (!payload.connection_id) return
      connectionId = payload.connection_id
      attempts = 0
      const isReconnect = everConnected
      everConnected = true
      granted.clear()
      denied.clear()
      setStatus('open')
      flush().then(() => {
        if (isReconnect) notifyReconnect()
      })
      return
    }

    let envelope
    try {
      envelope = JSON.parse(frame.data)
    } catch {
      console.warn('[realtime] unparseable event frame', frame)
      return
    }
    const { topic, type, data } = envelope || {}
    const set = registrations.get(topic)
    if (!set) return
    const evt = { type: type || frame.event, topic, data }
    for (const reg of [...set]) {
      try {
        reg.onEvent?.(evt)
      } catch (err) {
        console.error(`[realtime] handler for ${topic} failed`, err)
      }
    }
  }

  function notifyReconnect() {
    for (const set of [...registrations.values()]) {
      for (const reg of [...set]) {
        try {
          reg.onReconnect?.()
        } catch (err) {
          console.error('[realtime] onReconnect handler failed', err)
        }
      }
    }
  }

  function scheduleFlush() {
    if (flushScheduled) return
    flushScheduled = true
    queueMicrotask(() => {
      flushScheduled = false
      flush()
    })
  }

  async function flush() {
    if (!connectionId || flushing) return
    const desired = new Set(registrations.keys())
    const add = [...desired].filter((t) => !granted.has(t) && !denied.has(t))
    const remove = [...granted].filter((t) => !desired.has(t))
    if (add.length === 0 && remove.length === 0) return

    flushing = true
    const cid = connectionId
    // Optimistically mark adds as granted so a concurrent flush doesn't resend them;
    // verdicts below correct any denials.
    add.forEach((t) => granted.add(t))
    remove.forEach((t) => granted.delete(t))
    try {
      const resp = await client.post(`/api/v1/events/${cid}/subscriptions`, { add, remove })
      const results = resp?.results || {}
      for (const t of add) {
        if (results[t] !== 'granted') {
          granted.delete(t)
          denied.add(t)
          console.warn(`[realtime] subscription denied for topic "${t}"`)
        }
      }
    } catch (err) {
      // Roll back so a later flush retries; if the connection died, the reconnect
      // cycle re-subscribes everything anyway.
      add.forEach((t) => granted.delete(t))
      if (connectionId === cid) {
        console.warn('[realtime] subscription update failed', err)
      }
    } finally {
      flushing = false
      if (connectionId) scheduleFlush() // pick up drift accumulated while in flight
    }
  }

  function scheduleReconnect() {
    if (closed || reconnectTimer) return
    if (registrations.size === 0) {
      // nobody listening — reconnect lazily when the next subscriber arrives
      setStatus('idle')
      return
    }
    const delay = Math.min(backoffBaseMs * 2 ** attempts, backoffMaxMs)
    attempts += 1
    setStatus('reconnecting')
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, delay)
  }

  function startWatchdog(ac) {
    stopWatchdog()
    watchdogTimer = setInterval(() => {
      if (Date.now() - lastActivity > heartbeatTimeoutMs) {
        console.warn('[realtime] heartbeat timeout — recycling connection')
        ac.abort()
      }
    }, watchdogIntervalMs)
  }

  function stopWatchdog() {
    if (watchdogTimer) {
      clearInterval(watchdogTimer)
      watchdogTimer = null
    }
  }

  // Drop the current connection (if any) and reconnect immediately with a fresh
  // token. Used when the signed-in identity changes — the server binds a
  // connection to the identity that opened it.
  function reset() {
    attempts = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    connectionId = null
    granted.clear()
    denied.clear()
    if (abortController) {
      const ac = abortController
      abortController = null
      ac.abort()
    }
    if (closed) return
    if (registrations.size > 0) connect()
    else setStatus('idle')
  }

  function close() {
    if (closed) return
    closed = true
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    stopWatchdog()
    if (abortController) {
      const ac = abortController
      abortController = null
      ac.abort()
    }
    connectionId = null
    granted.clear()
    setStatus('closed')
  }

  // Reopen after close(). Exists so React StrictMode's mount→unmount→mount
  // simulation doesn't permanently kill the singleton manager.
  function open() {
    if (!closed) return
    closed = false
    setStatus('idle')
    if (registrations.size > 0) ensureConnected()
  }

  function onStatusChange(fn) {
    statusListeners.add(fn)
    return () => statusListeners.delete(fn)
  }

  return {
    subscribe,
    reset,
    open,
    close,
    getStatus: () => status,
    getConnectionId: () => connectionId,
    onStatusChange,
  }
}
